
import { useState } from 'react';
import { Download, ExternalLink, Trash2, X } from 'lucide-react';

import { libraryApi } from '../../api/endpoints';
import { ErrorBanner } from '../../components/ErrorBanner';
import { Button, ConfirmDialog, Pill, Skeleton } from '../../components/ui';
import { insertResolution, useDeleteInsert, useInsert } from '../../hooks';
import type { Id } from '../../types/api';
import { EnrichPanel } from './EnrichPanel';
import { formatDuration, untaggedNote } from './format';
import styles from './LibraryPage.module.css';

interface Props {
  itemId: Id;
  onClose: () => void;
  canEdit: boolean;
}

export function InsertDetail({ itemId, onClose, canEdit }: Props) {
  const { data: item, isLoading, error, refetch } = useInsert(itemId);
  const remove = useDeleteInsert();
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <aside className={styles.detail} aria-label="Карточка материала">
      <header className={styles.detailHead}>
        <span className={styles.detailHeadTitle}>Материал</span>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          className={styles.detailClose}
          onClick={onClose}
          aria-label="Закрыть карточку"
        >
          <X size={13} strokeWidth={1.8} />
        </Button>
      </header>

      <div className={styles.detailBody}>
        {isLoading && <Skeleton width="100%" height={168} />}

        {error?.isNotFound && (
          <div className={styles.notice}>Материал не найден, возможно его удалили.</div>
        )}
        {error && !error.isNotFound && (
          <ErrorBanner error={error} onRetry={() => void refetch()} />
        )}

        {item && (
          <>
            <div className={styles.detailFrame}>
              {item.media && item.media_type === 'video' && (
                <video src={item.media.url} controls preload="metadata" playsInline />
              )}
              {item.media && item.media_type === 'photo' && (
                <img className={styles.detailImage} src={item.media.url} alt={item.title} />
              )}
              {!item.media && (
                <div className={styles.panelHint}>Файл не открылся, обновите страницу.</div>
              )}
            </div>

            <h2 className={styles.detailTitle}>{item.title}</h2>
            {item.description && <p className={styles.detailText}>{item.description}</p>}

            {untaggedNote(item) && (
              <div className={styles.notice}>{untaggedNote(item)}</div>
            )}

            <Labels title="Теги" values={item.tags} empty="нет" />
            <Labels title="Настроение" values={item.mood} empty="нет" />
            <Labels title="Гео" values={item.geo} empty="нет" />
            <Labels title="Символы" values={item.symbolic_tags} empty="нет" />

            <dl className={styles.kv}>
              {formatDuration(item.duration_ms) && (
                <Row label="Длительность">{formatDuration(item.duration_ms)}</Row>
              )}
              {insertResolution(item) && (
                <Row label="Разрешение">{insertResolution(item)}</Row>
              )}
            </dl>

            <div className={styles.detailActions}>
              {item.media && (
                <a className={styles.linkAction} href={libraryApi.downloadUrl(item.id)}>
                  <Download size={13} strokeWidth={1.6} />
                  Скачать
                </a>
              )}
              {item.source_url && (
                <a
                  className={styles.linkAction}
                  href={item.source_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  <ExternalLink size={13} strokeWidth={1.6} />
                  Первоисточник
                </a>
              )}
              <Button
                variant="danger"
                onClick={() => setConfirmDelete(true)}
                disabled={!canEdit || remove.isPending}
                title={
                  canEdit ? 'Вернуть удалённое нельзя' : 'Роль «наблюдатель» не удаляет материалы'
                }
              >
                <Trash2 size={13} strokeWidth={1.6} />
                Удалить
              </Button>
            </div>

            {remove.error && <ErrorBanner error={remove.error} />}

            <EnrichPanel canEdit={canEdit} itemIds={[item.id]} />

            <ConfirmDialog
              open={confirmDelete}
              title="Удалить материал?"
              body={`«${item.title}» исчезнет вместе с файлом. Вернуть его будет нельзя.`}
              confirmLabel="Да, удалить"
              destructive
              busy={remove.isPending}
              onConfirm={() => remove.mutate(item.id, { onSuccess: () => onClose() })}
              onCancel={() => setConfirmDelete(false)}
            />
          </>
        )}
      </div>
    </aside>
  );
}

function Labels({
  title,
  values,
  empty,
}: {
  title: string;
  values: readonly string[];
  empty: string;
}) {
  return (
    <div className={styles.labelBlock}>
      <span className={styles.labelTitle}>{title}</span>
      {values.length === 0 ? (
        <span className={styles.panelHint}>{empty}</span>
      ) : (
        <span className={styles.pillRow}>
          {values.map((value) => (
            <Pill key={value} className={styles.pillClamp}>
              {value}
            </Pill>
          ))}
        </span>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={styles.kvRow}>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}
